import { FunctionComponent, useRef, useState } from 'react'
import { MenuButton, MenuButtonHandle } from '../common/MenuButton'
import {
  DeleteOutlineRounded,
  DownloadRounded,
  MoreVertRounded,
  RemoveCircleOutlineRounded,
  UploadRounded,
  VisibilityRounded,
} from '@mui/icons-material'
import { UploadDocumentModal } from './UploadDocumentModal'
import {
  CircularProgress,
  MenuItem,
  Typography,
  rucksack,
} from '@black-bear-energy/rucksack'
import { useDownloadChecklistDocument } from '../../api/hooks/useDownloadChecklistDocument'
import { useDeleteChecklistDocument } from '../../api/hooks/useDeleteChecklistDocument'
import { useDeleteChecklistDocumentReview } from '../../api/hooks/useDeleteChecklistDocumentReview'
import { FilesPreviewMenu } from '../common/FilesPreviewMenu'

export const DocumentMenu: FunctionComponent<DocumentMenuProps> = ({
  documentId,
  documentName,
  isUploaded,
  hasReview,
  isLimitedUser,
  isCollectionDocument = false,
}: DocumentMenuProps) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false)
  // Allows the child FilesPreviewMenu to close the parent menu
  const menuButtonRef = useRef<MenuButtonHandle>(null)
  const { mutate: downloadChecklistDocument } = useDownloadChecklistDocument()
  const { mutate: deleteChecklistDocument } = useDeleteChecklistDocument()
  const { mutate: deleteChecklistDocumentReview } =
    useDeleteChecklistDocumentReview()

  return (
    <>
      <MenuButton
        ref={menuButtonRef}
        icon={<MoreVertRounded />}
        align="right"
        menuSx={{
          '& .MuiMenuItem-root': {
            display: 'flex',
            padding: '6px 16px',
            alignItems: 'center',
            gap: '12px',
          },
        }}
        keepOpen={false}
        onClose={() => setIsMainMenuOpen(false)}
        onOpen={() => setIsMainMenuOpen(true)}
      >
        {isUploaded && (
          <MenuItem
            onClick={() => {
              downloadChecklistDocument({ documentId, documentName })
            }}
          >
            <DownloadRounded sx={{ color: rucksack.palette.action.active }} />
            <Typography sx={{ fontSize: '1rem' }}>Download Document</Typography>
          </MenuItem>
        )}
        {isUploaded && (
          <MenuItem
            onClick={() =>
              deleteChecklistDocument({ documentId, documentName })
            }
          >
            <DeleteOutlineRounded
              sx={{ color: rucksack.palette.action.active }}
            />
            <Typography sx={{ fontSize: '1rem' }}>Delete Document</Typography>
          </MenuItem>
        )}
        {isLimitedUser === false && hasReview && (
          <MenuItem
            onClick={() =>
              deleteChecklistDocumentReview({ documentId, documentName })
            }
          >
            <RemoveCircleOutlineRounded
              sx={{ color: rucksack.palette.action.active }}
            />
            <Typography sx={{ fontSize: '1rem' }}>Remove Review</Typography>
          </MenuItem>
        )}
        {isUploaded && (
          <FilesPreviewMenu
            documentId={documentId}
            documentName={documentName}
            onRequestClose={() => menuButtonRef.current?.close()}
            isMainMenuOpen={isMainMenuOpen}
          >
            {({ isLoading }) => (
              <MenuItem
                onMouseUp={(event) => {
                  // Prevent MenuButton's keepOpen={false} close-on-click from
                  // firing here, so the menu stays open while the files menu opens.
                  event.stopPropagation()
                }}
              >
                {isLoading ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      color: rucksack.palette.action.active,
                      marginLeft: '4px',
                      marginRight: '4px',
                    }}
                  />
                ) : (
                  <VisibilityRounded
                    sx={{ color: rucksack.palette.action.active }}
                  />
                )}
                <Typography sx={{ fontSize: '1rem', flexGrow: 1 }}>
                  View Document
                </Typography>
              </MenuItem>
            )}
          </FilesPreviewMenu>
        )}
        {/* Within a document collection, individual documents cannot be re-uploaded in place; new
            documents are added from the collection's three dot menu instead. */}
        {!isCollectionDocument && (
          <MenuItem
            onClick={() => {
              setUploadModalOpen(true)
            }}
          >
            <UploadRounded sx={{ color: rucksack.palette.action.active }} />
            <Typography sx={{ fontSize: '1rem' }}>
              Upload New Document
            </Typography>
          </MenuItem>
        )}
      </MenuButton>
      {!isCollectionDocument && (
        <UploadDocumentModal
          documentId={documentId}
          documentName={documentName}
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          hasExistingUpload={isUploaded}
        />
      )}
    </>
  )
}

export interface DocumentMenuProps {
  documentId: number
  documentName: string
  isUploaded: boolean
  hasReview: boolean
  isLimitedUser: boolean
  /**
   * True if this document belongs to a document collection. Collection documents cannot be
   * re-uploaded in place, so the "Upload New Document" option is hidden.
   */
  isCollectionDocument?: boolean
}
